import { useState } from 'react';
import { useAuth } from '../../contexts/useAuth';
import { impersonationApi } from '../../config/api';

export default function ImpersonationBanner() {
  const { impersonation, user, refreshUser } = useAuth();
  const [stopping, setStopping] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!impersonation.active || !impersonation.by) return null;

  const handleStop = async () => {
    setStopping(true);
    setError(null);
    try {
      await impersonationApi.stop();
      window.location.href = '/admin/subscriptions';
    } catch {
      setStopping(false);
      setError('Could not return to admin session. Please refresh and try again.');
    }
  };

  const handleToggleMode = async () => {
    setSwitching(true);
    setError(null);
    try {
      await impersonationApi.setMode(!impersonation.readOnly);
      await refreshUser();
    } catch {
      setError('Could not change mode. Please try again.');
    } finally {
      setSwitching(false);
    }
  };

  const viewingAs = user ? `${user.firstName} ${user.lastName}`.trim() : 'this user';
  const mode = impersonation.readOnly ? 'read-only' : 'edit';

  return (
    <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 dark:border-amber-500/20 dark:bg-amber-500/10">
      <div className="mx-auto flex max-w-(--breakpoint-2xl) flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2 text-sm text-amber-800 dark:text-amber-300">
          <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4a2 2 0 00-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z" />
          </svg>
          <span>
            Viewing as <strong>{viewingAs}</strong> — signed in as <strong>{impersonation.by.name}</strong>
            {' '}(<strong>{mode}</strong>)
          </span>
          {error && <span className="text-xs text-red-600 dark:text-red-400">{error}</span>}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleToggleMode}
            disabled={switching}
            className="rounded-lg border border-amber-600 px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-60 dark:text-amber-200 dark:hover:bg-amber-500/20"
          >
            {switching ? 'Switching…' : impersonation.readOnly ? 'Switch to edit' : 'Switch to read-only'}
          </button>
          <button
            type="button"
            onClick={handleStop}
            disabled={stopping}
            className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-amber-700 disabled:opacity-60"
          >
            {stopping ? 'Returning…' : 'Return to admin'}
          </button>
        </div>
      </div>
    </div>
  );
}
